import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { supplierApi } from "@/api/supplierClient";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/framework-originated/i18n";
import { useAuthStore } from "@/stores/auth";

// /supplier/settings — 个人设置(改密)。当前密码校验失败后端返回 400;成功后
// 刷新令牌(旧 session_token)一并吊销,下次操作需重新登录。
export function SupplierSettingsPage() {
  const { t } = useTranslation("supplier");
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const changeMut = useMutation({
    mutationFn: () => supplierApi.changePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setErr(null);
      setMsg(t("settings.updateSuccess"));
      logout();
      navigate({ to: "/supplier/login", replace: true });
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof ApiError ? e.message : t("settings.updateFailed"));
    },
  });

  const nextValid =
    next.length >= 8 &&
    next !== current &&
    confirm === next;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Supplier / settings</p>
        <h1 className="text-xl font-semibold">{t("settings.title")}</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("settings.description")}</p>
      </div>

      {(msg || err) && (
        <div
          className={`rounded-md border px-3 py-2 text-sm ${
            err ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-green-200 bg-green-50 text-green-800"
          }`}
        >
          {err ?? msg}
        </div>
      )}

      <section className="max-w-md rounded-xl border bg-card shadow-sm">
        <header className="border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{t("settings.changePassword")}</h2>
        </header>
        <form
          className="space-y-4 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (nextValid) changeMut.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="cur">{t("settings.currentPassword")} *</Label>
            <Input
              id="cur"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="next">{t("settings.newPassword")} *</Label>
            <Input
              id="next"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
            <p className="text-[11px] text-muted-foreground">{t("settings.newPasswordHint")}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm">{t("settings.confirmPassword")} *</Label>
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <Button type="submit" disabled={!nextValid || changeMut.isPending}>
            {changeMut.isPending ? t("settings.submitting") : t("settings.updatePassword")}
          </Button>
        </form>
      </section>
    </div>
  );
}
